<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";

import { useUser } from "@/composables/api/useUser";
import { rewards } from "@/rewards";

import LockClosedIcon from "@/components/icons/LockClosedIcon.vue";
import LockOpenedIcon from "@/components/icons/LockOpenedIcon.vue";
import RadialProgress from "@/components/ui/RadialProgress.vue";

const props = defineProps<{
  points: number;
}>();

const { data: user } = useUser();

const MOBILE_DIAMETER = 38;
const DESKTOP_DIAMETER = 64;

const { width } = useWindowSize();

const currentRewards = computed(() => {
  if (!user.value?.current_mechanic) {
    return [];
  }
  return rewards[user.value?.current_mechanic];
});
// Find the next reward that hasn't been unlocked yet.
const currentUnlockingIndex = computed(() => {
  const nextRewardIndex = currentRewards.value.findIndex((reward) => reward.points > props.points);
  return nextRewardIndex >= 0 ? nextRewardIndex : null;
});

const currentProgress = computed(() => {
  // All rewards unlocked.
  if (currentUnlockingIndex.value === null) {
    return 100;
  }

  // Progress towards first reward.
  if (currentUnlockingIndex.value === 0) {
    return (props.points / currentRewards.value[0].points) * 100;
  }

  // Get previous and next reward points.
  const previousPoints = currentRewards.value[currentUnlockingIndex.value - 1].points;
  const nextPoints = currentRewards.value[currentUnlockingIndex.value].points;

  // Calculate progress between previous and next threshold.
  const progressInRange = props.points - previousPoints;
  const rangeSize = nextPoints - previousPoints;

  return (progressInRange / rangeSize) * 100;
});

const diameter = computed(() => (width.value < 1024 ? MOBILE_DIAMETER : DESKTOP_DIAMETER));
</script>

<template>
  <div
    class="relative flex items-center gap-5"
    role="list"
    aria-label="Progression des récompenses"
  >
    <span
      role="presentation"
      aria-hidden="true"
      class="bg-galbani absolute top-1/2 right-0 left-0 h-px w-full -translate-y-1/2"
    />

    <span
      v-for="(reward, index) in currentRewards"
      :key="reward.name"
      :class="
        twMerge(
          'relative flex size-8 items-center justify-center rounded-full lg:size-14',
          points >= reward.points &&
            user?.current_mechanic !== 'lactel' &&
            'bg-galbani border-galbani border-[3px] text-white',
          points >= reward.points &&
            user?.current_mechanic === 'lactel' &&
            'bg-lactel-blue-400 border-lactel-blue-400 border-[3px] text-white',

          points < reward.points && 'text-secondary bg-white',
        )
      "
      role="listitem"
      :aria-label="`${$t(reward.name)} - ${reward.points} points`"
    >
      <LockOpenedIcon v-if="points >= reward.points" class="size-4 lg:size-8" aria-hidden="true" />

      <span v-if="points >= reward.points" class="sr-only">Débloqué</span>

      <template v-else>
        <LockClosedIcon class="size-4 lg:size-8" aria-hidden="true" />

        <span class="sr-only">Verrouillé</span>

        <RadialProgress
          class="absolute inset-[-3px]"
          :diameter="diameter"
          :progress="index === currentUnlockingIndex ? currentProgress : 0"
          :stroke-width="3"
          strokeColor="#214C9F"
          innerStrokeColor="#9B9B9B"
          :inner-stroke-width="3"
          aria-hidden="true"
        />
      </template>
    </span>
  </div>
</template>
